import {
  ReactElement,
  ReactNode,
  RefObject,
  useLayoutEffect,
  useState,
} from "react";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import themeConfig from "../../theme/themeConfig.json";
import type { ActionMenuProps } from "./actionMenu.interface";

const MenuTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "calc(100vw - 24px)",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    padding: "16px 20px",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: themeConfig.shadows.card,
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  [`&&[data-popper-placement] .${tooltipClasses.tooltip}`]: {
    margin: 0,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
    "&::before": { border: "1px solid #E2E8F0" },
  },
}));

function ActionMenuTooltip({
  title,
  children,
  menuRef,
  itemRef,
  size = "compact",
  open,
  onOpen,
  onClose,
}: {
  title?: ReactNode;
  children: ReactElement;
  menuRef: RefObject<HTMLElement>;
  itemRef: RefObject<HTMLElement>;
  size?: ActionMenuProps["tooltipSize"];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const [menuHeight, setMenuHeight] = useState(0);
  const matchMenu = size === "menu";

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!matchMenu || !open || !menu) return;
    const measure = () => setMenuHeight(menu.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(menu);
    return () => observer.disconnect();
  }, [matchMenu, open, menuRef]);

  const getOffset = ({ placement }: { placement: string }) => {
    if (!matchMenu) return [0, 12];
    const menu = menuRef.current?.getBoundingClientRect();
    const item = itemRef.current?.getBoundingClientRect();
    if (!menu || !item) return [0, 12];

    // Aliniaza cardul la meniu; sageata ramane ancorata la itemul activ.
    switch (placement.split("-")[0]) {
      case "left":
        return [menu.top - item.top, item.left - menu.left + 12];
      case "right":
        return [menu.top - item.top, menu.right - item.right + 12];
      case "top":
        return [menu.left - item.left, item.top - menu.top + 12];
      default:
        return [menu.left - item.left, menu.bottom - item.bottom + 12];
    }
  };

  if (!title) return children;

  return (
    <MenuTooltip
      title={
        <div
          className="w-full overflow-y-auto"
          style={{
            maxHeight:
              matchMenu && menuHeight
                ? `min(${menuHeight - 34}px, calc(100vh - 58px))`
                : "min(206px, calc(100vh - 58px))",
          }}>
          {title}
        </div>
      }
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      arrow
      describeChild
      placement={matchMenu ? "left-start" : "left"}
      enterDelay={350}
      enterNextDelay={0}
      leaveDelay={100}
      TransitionProps={{ timeout: 0 }}
      componentsProps={{
        tooltip: {
          sx: {
            width: matchMenu ? 360 : 300,
            minHeight: matchMenu ? undefined : 88,
            height:
              matchMenu && menuHeight
                ? `min(${menuHeight}px, calc(100vh - 24px))`
                : undefined,
          },
        },
      }}
      PopperProps={{
        modifiers: [
          { name: "offset", options: { offset: getOffset } },
          {
            name: "flip",
            options: {
              fallbackPlacements: matchMenu
                ? ["right-start", "top-start", "bottom-start"]
                : ["right", "top", "bottom"],
              padding: 12,
            },
          },
          {
            name: "preventOverflow",
            options: { padding: 12, altAxis: true },
          },
        ],
      }}>
      {children}
    </MenuTooltip>
  );
}

export default ActionMenuTooltip;
